import { PreconditionFailedError } from "./errors";
import type { Finding, FindingRecordId, FindingState } from "./finding.model";
import type { CanonicalLineage } from "./lib/canonical";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import { type MaterializableDefinition, materializeDefinition, type VersionedCanonical } from "./spine.model";
import type { Visit, VisitId } from "./visit.model";

// ============================================================
// Exam — a patient-owned panel of finding records
//
// The pinned exam definition arranges unpinned finding lineages. Each member
// pins its own offered revision. Close-out checks documentation, with skipped
// prerequisites propagated at that point; confirmed bulk DNP documents remaining
// placements before close-out. It is independent of visit closure.
// ============================================================

export type ExamId = DomainId<"ExamId">;
export function asExamId(value: string): ExamId {
  if (!value.trim()) throw new PreconditionFailedError("An Exam identity cannot be empty");
  return value as ExamId;
}

export type ExamStatus = "preliminary" | "final";
export const EXAM_OTHER_OPTION_ID = "other";
export type FindingId = CanonicalLineage;
export function asFindingId(value: string): FindingId {
  if (!value.trim()) throw new PreconditionFailedError("A finding lineage cannot be empty");
  return value as FindingId;
}
export type ExamSelection = string;
export type ExamFindingState = FindingState;

export interface ExamDefinitionSpec {
  url: string;
  version: number;
  sections: readonly { findings: readonly { finding: string; prerequisite?: string }[] }[];
}

export interface ExamFacts {
  id: ExamId;
  definition: VersionedCanonical;
  patient: PatientId;
  visit: VisitId;
  status: ExamStatus;
  members: readonly FindingRecordId[];
}
export interface ExamCreationFacts extends Omit<ExamFacts, "status" | "members" | "definition"> {
  definition: MaterializableDefinition;
}

export class Exam {
  private data: ExamFacts;

  constructor(facts: ExamCreationFacts) {
    if (!facts.patient.trim() || !facts.visit.trim())
      throw new PreconditionFailedError("An exam requires a patient and opening visit");
    this.data = {
      ...facts,
      id: asExamId(facts.id),
      definition: materializeDefinition(facts.definition),
      status: "preliminary",
      members: [],
    };
  }

  static restore(facts: ExamFacts): Exam {
    const exam = Object.create(Exam.prototype) as Exam;
    exam.data = structuredClone(facts);
    return exam;
  }

  static openFromVisit(visit: Visit, facts: Omit<ExamCreationFacts, "patient" | "visit">): Exam {
    return new Exam({ ...facts, patient: visit.patientId, visit: visit.id });
  }

  assertPatient(patient: PatientId): void {
    if (patient !== this.data.patient) throw new PreconditionFailedError("This exam belongs to another patient");
  }

  get id(): ExamId {
    return this.data.id;
  }
  get status(): ExamStatus {
    return this.data.status;
  }
  get facts(): ExamFacts {
    return structuredClone(this.data);
  }
  snapshot(): ExamFacts {
    return this.facts;
  }

  followReschedule(from: Visit, to: Visit): void {
    if (from.id !== this.data.visit || from.status !== "cancelled" || to.patientId !== this.data.patient) {
      throw new PreconditionFailedError("This exam does not belong to the rescheduled visit");
    }
    this.data.visit = to.id;
  }

  assertFindingRetractable(findingId: FindingRecordId): void {
    if (this.data.status === "final" && this.data.members.includes(findingId))
      throw new PreconditionFailedError("A finding in a closed out exam cannot be retracted");
  }

  addFinding(definition: ExamDefinitionSpec, finding: Finding, members: readonly Finding[]): void {
    this.assertDefinition(definition);
    if (this.data.status !== "preliminary") throw new PreconditionFailedError("An exam is already closed out");
    const facts = finding.facts;
    if (facts.patient !== this.data.patient || facts.status !== "final")
      throw new PreconditionFailedError("An exam member must be an active finding for its patient");
    if (
      !definition.sections.some((section) =>
        section.findings.some((placement) => placement.finding === facts.definition.url),
      )
    ) {
      throw new PreconditionFailedError("This finding is not placed in the exam");
    }
    if (this.activeMembers(members).some((member) => member.facts.definition.url === facts.definition.url)) {
      throw new PreconditionFailedError("This exam placement already has a finding");
    }
    this.data.members = [...this.data.members, finding.id];
  }

  prerequisiteCascade(
    definition: ExamDefinitionSpec,
    members: readonly Finding[],
  ): readonly { finding: string; state: Exclude<FindingState, { kind: "Performed" }> }[] {
    this.assertDefinition(definition);
    const placements = definition.sections.flatMap((section) => section.findings);
    const states = new Map(
      this.activeMembers(members)
        .filter((member) => member.facts.state.kind !== "Unanswered")
        .map((member) => [member.facts.definition.url, member.facts.state]),
    );
    const cascaded = new Map<string, Exclude<FindingState, { kind: "Performed" }>>();
    for (let pass = 0; pass < placements.length; pass++) {
      for (const placement of placements) {
        const prerequisite = placement.prerequisite && states.get(placement.prerequisite);
        if (
          prerequisite &&
          prerequisite.kind !== "Performed" &&
          states.get(placement.finding)?.kind !== prerequisite.kind
        ) {
          states.set(placement.finding, prerequisite);
          cascaded.set(placement.finding, prerequisite);
        }
      }
    }
    return [...cascaded].map(([finding, state]) => ({ finding, state: structuredClone(state) }));
  }

  readinessViolations(
    definition: ExamDefinitionSpec,
    members: readonly Finding[],
  ): readonly { code: "UNRECORDED_FINDING" | "REQUIRED_COMMENT_MISSING"; findingId: FindingId; message: string }[] {
    this.assertDefinition(definition);
    const byLineage = new Map(this.activeMembers(members).map((member) => [member.facts.definition.url, member.facts]));
    return definition.sections
      .flatMap((section) => section.findings)
      .flatMap<{ code: "UNRECORDED_FINDING" | "REQUIRED_COMMENT_MISSING"; findingId: FindingId; message: string }>(
        (placement) => {
          const finding = byLineage.get(placement.finding);
          if (!finding || finding.state.kind === "Unanswered")
            return [
              {
                code: "UNRECORDED_FINDING" as const,
                findingId: asFindingId(placement.finding),
                message: "Finding has not been documented",
              },
            ];
          if (
            finding.state.kind === "Performed" &&
            finding.state.answer.kind === "Choice" &&
            finding.state.answer.selections.includes("other") &&
            !finding.comment?.trim()
          ) {
            return [
              {
                code: "REQUIRED_COMMENT_MISSING" as const,
                findingId: asFindingId(placement.finding),
                message: "Other requires a comment",
              },
            ];
          }
          return [];
        },
      );
  }

  closeOut(definition: ExamDefinitionSpec, members: readonly Finding[]): void {
    if (this.data.status !== "preliminary") throw new PreconditionFailedError("An exam is already closed out");
    if (this.prerequisiteCascade(definition, members).length)
      throw new PreconditionFailedError("Prerequisite outcomes must be documented before close-out");
    const violations = this.readinessViolations(definition, members);
    if (violations.length)
      throw new PreconditionFailedError(violations.map((violation) => violation.message).join("; "));
    this.data.status = "final";
  }

  private activeMembers(members: readonly Finding[]): Finding[] {
    if (this.data.members.some((id) => !members.some((member) => member.id === id)))
      throw new PreconditionFailedError("An exam member is missing");
    return members.filter((member) => this.data.members.includes(member.id) && member.facts.status === "final");
  }

  private assertDefinition(definition: ExamDefinitionSpec): void {
    if (definition.url !== this.data.definition.url || String(definition.version) !== this.data.definition.version)
      throw new PreconditionFailedError("The exam requires its pinned definition");
  }
}
